import { useCallback, useEffect, useState } from "react";
import { usePreviewStorage } from "./use-preview-storage";

import type { UUID } from "../../../../../../../utils/uuid";
import type { MomentRecipientsClassify } from "../../../../../hooks/use-moment-recipients-classify";
import type { PreviewDataRecipient } from "../../../../../server/supabase-preview-service";

export type PreviewSubject = Omit<
  PreviewDataRecipient,
  | "connection_id"
  | "connection_first_name"
  | "connection_last_name"
  | "connection_email"
  | "connection_display"
> & {
  connection?: {
    id: UUID;
    email: string;
    first_name: string;
    last_name: string;
    display: string;
  };
};

export type PreviewRecipientInfo = {
  subject: PreviewSubject;
  recipient: NonNullable<PreviewSubject["connection"]>;
} | null;

export function useSelectedPreviewInfo({
  parsedData,
  classifyData,
  momentId,
}: {
  parsedData: PreviewSubject[];
  classifyData: MomentRecipientsClassify | null;
  momentId: UUID;
}): [PreviewRecipientInfo, (subjectId: UUID | null) => void] {
  const [selected, setSelected] = useState<PreviewRecipientInfo>(null);

  const { value, set } = usePreviewStorage();

  const setSelectedPreviewInfo = useCallback(
    (subjectId: UUID | null) => {
      set((prev) => {
        const next = { ...prev };
        if (subjectId === null) {
          delete next[momentId];
          return next;
        }
        return { ...next, [momentId]: subjectId };
      });

      const subject = parsedData.find((x) => x.id === subjectId);
      if (!subject) return setSelected(null);

      if (classifyData?.type === "connection") {
        const { connection } = subject;
        if (!connection) return setSelected(null);

        return setSelected({ subject, recipient: connection });
      }

      setSelected({ subject, recipient: subject });
    },
    [parsedData, classifyData, momentId, set],
  );

  // Sync from sessionStorage
  useEffect(() => {
    if (selected) return;
    const storedId = value?.[momentId];
    if (!storedId) return;

    const subject = parsedData.find((x) => x.id === storedId);
    if (!subject) return;

    if (classifyData?.type === "connection") {
      const { connection } = subject;
      if (!connection) return;

      return setSelected({ subject, recipient: connection });
    }

    setSelected({ subject, recipient: subject });
  }, [value, momentId, parsedData, classifyData, selected]);

  return [selected, setSelectedPreviewInfo];
}
